import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';

interface FormularioMensagem {
  titulo: string;
  conteudo: string;
}

@Component({
  selector: 'app-mensagem-automatica',
  templateUrl: './mensagem-automatica.component.html',
  styleUrls: ['./mensagem-automatica.component.scss']
})
export class MensagemAutomaticaComponent implements OnInit {

  // Formulários específicos
  formCadastro: FormGroup;
  formSolicitacaoSemAgendamento: FormGroup;
  formSolicitacaoComAgendamento: FormGroup;
  formConfirmacaoAtendimento: FormGroup;
  formConfirmacao24h: FormGroup;
  loading = false;

  // IDs existentes por tipo (usados para update)
  private idCadastroPlataforma = 0;
  private idSemAgendamento = 0;
  private idComAgendamento = 0;
  private idConfirmacao = 0;
  private idConfirmacao24h = 0;

  constructor(
    private fb: FormBuilder,
    private ajaxService: AjaxService,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.inicializarFormularios();
    this.carregarMensagens();
  }

  inicializarFormularios(): void {
    // Formulário 1: Mensagem Cadastro na plataforma
    this.formCadastro = this.fb.group({
      titulo: ['Mensagem Cadastro na plataforma', Validators.required],
      conteudo: ['', [Validators.required]]
    });

    // Formulário 2: Mensagem solicitação de serviço sem agendamento
    this.formSolicitacaoSemAgendamento = this.fb.group({
      titulo: ['Mensagem solicitação de serviço sem agendamento', Validators.required],
      conteudo: ['', [Validators.required]]
    });

    // Formulário 3: Mensagem solicitação de serviço com agendamento
    this.formSolicitacaoComAgendamento = this.fb.group({
      titulo: ['Mensagem solicitação de serviço com agendamento', Validators.required],
      conteudo: ['', [Validators.required]]
    });

    // Formulário 4: Mensagem confirmação de atendimento
    this.formConfirmacaoAtendimento = this.fb.group({
      titulo: ['Mensagem confirmação de atendimento', Validators.required],
      conteudo: ['', [Validators.required]]
    });

    // Formulário 5: Mensagem confirmação 24hs antes do atendimento
    this.formConfirmacao24h = this.fb.group({
      titulo: ['Mensagem confirmação 24hs antes do atendimento', Validators.required],
      conteudo: ['', [Validators.required]]
    });
  }

  carregarMensagens(): void {
    this.loading = true;
    this.util.showLoading();

    const url = API + 'empresa/NotificacaoMensagem/BuscarMensagens';
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();

        const data = response?.data ? this.util.converterParaLowercase(response.data) : null;
        if (!data) return;

        const cadastro = this.util.converterParaLowercase(data.cadastroplataforma || {});
        const semAgendamento = this.util.converterParaLowercase(data.solicitacaoservicosemagendamento || {});
        const comAgendamento = this.util.converterParaLowercase(data.solicitacaoservicocomagendamento || {});
        const confirmacao = this.util.converterParaLowercase(data.confirmacaoconsulta || {});
        const confirmacao24 = this.util.converterParaLowercase(data.confirmacaoconsulta24hsantes || {});

        this.idCadastroPlataforma = Number(cadastro.id_notificacao_mensagem || 0);
        this.idSemAgendamento = Number(semAgendamento.id_notificacao_mensagem || 0);
        this.idComAgendamento = Number(comAgendamento.id_notificacao_mensagem || 0);
        this.idConfirmacao = Number(confirmacao.id_notificacao_mensagem || 0);
        this.idConfirmacao24h = Number(confirmacao24.id_notificacao_mensagem || 0);

        this.formCadastro.patchValue({ conteudo: cadastro.texto || '' });
        this.formSolicitacaoSemAgendamento.patchValue({ conteudo: semAgendamento.texto || '' });
        this.formSolicitacaoComAgendamento.patchValue({ conteudo: comAgendamento.texto || '' });
        this.formConfirmacaoAtendimento.patchValue({ conteudo: confirmacao.texto || '' });
        this.formConfirmacao24h.patchValue({ conteudo: confirmacao24.texto || '' });
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao carregar mensagens automáticas');
      }
    });
  }

  // Métodos para salvar cada formulário específico
  salvarFormCadastro(): void {
    this.salvarMensagem(
      this.formCadastro,
      1,
      this.idCadastroPlataforma,
      (id) => this.idCadastroPlataforma = id,
      'Mensagem de cadastro salva com sucesso!'
    );
  }

  salvarFormSolicitacaoSemAgendamento(): void {
    this.salvarMensagem(
      this.formSolicitacaoSemAgendamento,
      2,
      this.idSemAgendamento,
      (id) => this.idSemAgendamento = id,
      'Mensagem de solicitação sem agendamento salva com sucesso!'
    );
  }

  salvarFormSolicitacaoComAgendamento(): void {
    this.salvarMensagem(
      this.formSolicitacaoComAgendamento,
      3,
      this.idComAgendamento,
      (id) => this.idComAgendamento = id,
      'Mensagem de solicitação com agendamento salva com sucesso!'
    );
  }

  salvarFormConfirmacaoAtendimento(): void {
    this.salvarMensagem(
      this.formConfirmacaoAtendimento,
      4,
      this.idConfirmacao,
      (id) => this.idConfirmacao = id,
      'Mensagem de confirmação de atendimento salva com sucesso!'
    );
  }

  salvarFormConfirmacao24h(): void {
    this.salvarMensagem(
      this.formConfirmacao24h,
      5,
      this.idConfirmacao24h,
      (id) => this.idConfirmacao24h = id,
      'Mensagem de confirmação 24h salva com sucesso!'
    );
  }

  // Métodos auxiliares para validação
  marcarCamposInvalidosForm(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }

  isFieldInvalidForm(form: FormGroup, fieldName: string): boolean {
    const field = form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  getFieldErrorForm(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'Este campo é obrigatório.';
      }
      if (field.errors['minlength']) {
        return `Mínimo de ${field.errors['minlength'].requiredLength} caracteres.`;
      }
    }
    return '';
  }

  private salvarMensagem(
    form: FormGroup,
    idTipo: number,
    idMensagemAtual: number,
    atualizarId: (id: number) => void,
    mensagemSucesso: string
  ): void {
    if (!form.valid) {
      this.marcarCamposInvalidosForm(form);
      this.util.toasterShowError('Validação', 'O campo de mensagem não pode ser vazio.');
      return;
    }

    const payload = {
      ID_NOTIFICACAO_MENSAGEM: idMensagemAtual || 0,
      ID_NOTIFICACAO_TIPO: idTipo,
      TEXTO: (form.value?.conteudo || '').trim()
    };

    this.loading = true;
    this.util.showLoading();

    const url = API + 'empresa/NotificacaoMensagem/Save';
    this.ajaxService.post(url, payload).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();

        const data = response?.data ? this.util.converterParaLowercase(response.data) : null;
        const novoId = Number(data?.id_notificacao_mensagem || data?.id || idMensagemAtual || 0);
        if (novoId > 0) {
          atualizarId(novoId);
        }

        this.util.toasterShowSuccess('Sucesso', mensagemSucesso);
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível salvar a mensagem automática.');
      }
    });
  }
} 